import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import { Delete, Work } from '@mui/icons-material';
import { useCanAccess, useNotify } from 'react-admin';
import { ProjectFlowStepComment } from '@/types';
import { formatFlowDate } from './projectFlowUtils';
import { useDeleteStepComment } from './useProjectFlow';
import { fetchStepComments, resolvePhotoUrl } from './projectFlowApi';
import { FileGallery } from '@/components/FileGallery';

interface StepCommentsProps {
    projectId: number;
    stepId: number;
    comments: ProjectFlowStepComment[];
    /** Total number of non-deleted comments on this step (from the backend). */
    totalCount?: number;
    currentUserId?: number;
}

const PAGE_SIZE = 5;

/** Matches an auto-generated job link `[Title](/cards/123/show)` in a comment body. */
const JOB_LINK_RE = /\[([^\]]+)\]\((\/cards\/\d+\/show)\)/;

/**
 * Renders a comment body. A completion comment carries a `[name](/cards/id/show)`
 * marker, which we strip from the text and render as a clickable job chip.
 * Any comment without that marker renders verbatim.
 */
const CommentBody: React.FC<{ content: string }> = ({ content }) => {
    const navigate = useNavigate();
    const match = content.match(JOB_LINK_RE);

    if (!match) {
        return (
            <Typography variant="body2" sx={{ mt: 0.25, whiteSpace: 'pre-wrap', fontSize: 13 }}>
                {content}
            </Typography>
        );
    }

    const [, label, path] = match;
    const text = content
        .replace(/\n*\s*(?:Job:\s*)?\[[^\]]+\]\(\/cards\/\d+\/show\)\s*/, '')
        .trim();

    return (
        <Box sx={{ mt: 0.25 }}>
            {text && (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: 13, mb: 0.75 }}>
                    {text}
                </Typography>
            )}
            <Chip
                size="small"
                clickable
                icon={<Work sx={{ fontSize: 16 }} />}
                label={label}
                onClick={() => navigate(path)}
            />
        </Box>
    );
};

export const StepComments: React.FC<StepCommentsProps> = ({
    projectId,
    stepId,
    comments,
    totalCount,
    currentUserId,
}) => {
    const [extra, setExtra] = React.useState<ProjectFlowStepComment[]>([]);
    const [loadingMore, setLoadingMore] = React.useState(false);
    const [exhausted, setExhausted] = React.useState(false);

    const { canAccess: canDelete } = useCanAccess({
        resource: 'project_flow_comment',
        action: 'delete',
    });
    const deleteCommentMutation = useDeleteStepComment();
    const notify = useNotify();

    // Reset "load more" buffer when the base comments change (invalidate / new post).
    React.useEffect(() => {
        setExtra([]);
        setExhausted(false);
    }, [comments]);

    const all = React.useMemo(() => {
        const seen = new Set<number>();
        return [...comments, ...extra].filter((c) => {
            if (seen.has(c.id)) return false;
            seen.add(c.id);
            return true;
        });
    }, [comments, extra]);

    const isAuthor = (comment: ProjectFlowStepComment) =>
        currentUserId != null && comment.created_by === currentUserId;

    const canDeleteComment = (comment: ProjectFlowStepComment) =>
        isAuthor(comment) || canDelete;

    const handleDeleteComment = (comment: ProjectFlowStepComment) => {
        if (!window.confirm('Delete this comment?')) return;
        deleteCommentMutation.mutate({ projectId, commentId: comment.id });
    };

    const handleLoadMore = async () => {
        setLoadingMore(true);
        try {
            const next = await fetchStepComments(projectId, stepId, {
                limit: PAGE_SIZE,
                offset: all.length,
            });
            if (next.length === 0) {
                setExhausted(true);
                return;
            }
            setExtra((prev) => [...prev, ...next]);
            if (next.length < PAGE_SIZE) setExhausted(true);
        } catch (error) {
            notify(
                error instanceof Error
                    ? error.message
                    : 'Failed to load more comments',
                { type: 'error' },
            );
        } finally {
            setLoadingMore(false);
        }
    };

    if (all.length === 0) {
        return (
            <Typography variant="caption" color="textSecondary" sx={{ py: 1 }}>
                No comments yet
            </Typography>
        );
    }

    return (
        <Stack gap={0.75} sx={{ py: 0.5 }}>
            {all.map((comment, idx) => {
                const author = isAuthor(comment);
                return (
                    <Box
                        key={comment.id}
                        sx={{
                            px: 1,
                            py: 0.75,
                            borderRadius: 1,
                            borderLeft: '3px solid',
                            borderColor: author
                                ? 'primary.main'
                                : 'divider',
                            bgcolor:
                                idx % 2 === 0
                                    ? 'action.hover'
                                    : 'background.paper',
                            transition:
                                'background-color 120ms ease, box-shadow 120ms ease, border-color 120ms ease',
                            '&:hover': {
                                bgcolor: 'action.selected',
                                borderColor: author
                                    ? 'primary.dark'
                                    : 'text.secondary',
                                boxShadow: 1,
                            },
                        }}
                    >
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            gap={1}
                        >
                            <Stack direction="row" gap={1} alignItems="baseline">
                                <Typography
                                    variant="caption"
                                    fontWeight="bold"
                                >
                                    {comment.created_by_name || 'Unknown'}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                >
                                    {formatFlowDate(comment.created_at)}
                                </Typography>
                            </Stack>
                            {canDeleteComment(comment) && (
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        handleDeleteComment(comment)
                                    }
                                    disabled={
                                        deleteCommentMutation.isPending
                                    }
                                    aria-label="Delete comment"
                                    sx={{ p: 0.25 }}
                                >
                                    {deleteCommentMutation.isPending &&
                                    deleteCommentMutation.variables?.commentId ===
                                        comment.id ? (
                                        <CircularProgress size={14} />
                                    ) : (
                                        <Delete sx={{ fontSize: 16 }} />
                                    )}
                                </IconButton>
                            )}
                        </Stack>

                        {comment.content && <CommentBody content={comment.content} />}

                        <FileGallery
                            files={comment.photos}
                            resolveUrl={resolvePhotoUrl}
                        />
                    </Box>
                );
            })}

            {!exhausted &&
                (totalCount == null
                    ? all.length >= PAGE_SIZE
                    : all.length < totalCount) && (
                    <Button
                        size="small"
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                        sx={{
                            alignSelf: 'flex-start',
                            textTransform: 'none',
                        }}
                    >
                        {loadingMore ? 'Loading…' : 'Load more comments'}
                    </Button>
                )}
        </Stack>
    );
};

StepComments.displayName = 'StepComments';
